import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import { useMemo, useSyncExternalStore } from 'react';
import { ArrayPath, FieldValues, Path, PathValue, Validate } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { isNewDesignCopy, onNewDesignCopyChange } from 'utils/newDesignCopy';
import { stringIsNotWhitespaceOnly } from '../rhf-validators/validators';

type UseMemoizedRulesProps<T extends FieldValues, TPath extends Path<T> | ArrayPath<T> = Path<T>> = {
    required?: boolean;
    /** What an empty required field is told. A picker names choosing, where the default names filling in. */
    requiredMessageId?: string;
    validate?: Validate<PathValue<T, TPath>, T> | Record<string, Validate<PathValue<T, TPath>, T>> | undefined;
};

export const useMemoizedRules = <T extends FieldValues, TPath extends Path<T> | ArrayPath<T>>({
    required,
    requiredMessageId = 'validation.required-field',
    validate,
}: UseMemoizedRulesProps<T, TPath>) => {
    // `t()` words the message in the design in use. Following its catalog re-renders the field when the design is
    // switched under a form that stays mounted, such as one in a modal, and the rules are rebuilt on the new wording.
    useSyncExternalStore(onNewDesignCopyChange, isNewDesignCopy);
    const requiredMessage = t(requiredMessageId);
    // A draft is saved with its required fields empty: the rule and its whitespace twin are off for that save.
    const drafting = useDraftSaveMode();
    const isRequired = !!required && !drafting;
    return useMemo(() => {
        const originalValidationRules = typeof validate === 'function' ? { default: validate } : validate;
        return {
            required: { value: isRequired, message: requiredMessage },
            validate: {
                ...originalValidationRules,
                ...(isRequired ? { whitespaces: stringIsNotWhitespaceOnly(requiredMessage) } : {}),
            },
        };
    }, [isRequired, requiredMessage, validate]);
};
